import { useState, useEffect, useCallback } from 'react';
import { projectService } from '../services/projectService';

/**
 * Hook for managing project context across asset types
 * Ensures consistent project settings and context persistence
 */
export function useProjectContext(projectId) {
  const [context, setContext] = useState({
    text: '',
    files: [],
    tags: [],
    uploads: []
  });
  const [loading, setLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Load project context
  const loadContext = useCallback(async () => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      const project = await projectService.getProject(projectId);
      
      // Parse context text
      const ctxArr = Array.isArray(project?.context)
        ? project.context
        : (typeof project?.context === 'string' && project.context.length
            ? String(project.context).split(/\n+/).map(s => s.trim()).filter(Boolean)
            : []);
      
      // Parse tags
      const tags = (project?.goals && typeof project.goals === 'string')
        ? project.goals.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      
      // Parse files
      const files = Array.isArray(project?.contextFiles) ? project.contextFiles : [];
      
      const newContext = {
        text: ctxArr.join('\n'),
        files,
        tags,
        uploads: files.map(url => ({
          url,
          name: url.split('/').pop() || url,
          type: url.match(/\.(png|jpg|jpeg|webp|gif|svg|pdf)$/i) ? 'image' : 'file',
          persisted: true
        }))
      };
      
      setContext(newContext);
      setHasChanges(false);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Failed to load project context:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Save project context
  const saveContext = useCallback(async (newContext = null) => {
    if (!projectId) return false;
    
    const contextToSave = newContext || context;
    
    try {
      setLoading(true);
      
      const payload = {
        context: contextToSave.text.split(/\n+/).map(s => s.trim()).filter(Boolean).join('\n'),
        contextFiles: [
          ...contextToSave.files,
          ...contextToSave.uploads.filter(item => item?.url).map(item => item.url)
        ],
        goals: contextToSave.tags.join(', ')
      };
      
      await projectService.saveProjectContext(projectId, payload);
      setHasChanges(false);
      setLastSaved(new Date());
      return true;
    } catch (error) {
      console.error('Failed to save project context:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [projectId, context]);

  // Update context with debounced change detection
  const updateContext = useCallback((updates) => {
    setContext(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  }, []);

  // Auto-save with debounce
  useEffect(() => {
    if (!hasChanges || !projectId) return;
    
    const timeout = setTimeout(() => {
      saveContext();
    }, 2000); // 2 second debounce
    
    return () => clearTimeout(timeout);
  }, [hasChanges, projectId, saveContext]);

  // Load context on project change
  useEffect(() => {
    loadContext();
  }, [loadContext]);

  return {
    context,
    updateContext,
    saveContext,
    loadContext,
    loading,
    lastSaved,
    hasChanges
  };
}
